import { FC } from "react";
import Link from "next/link";
import { gql } from "@apollo/client";
import { Badge } from "@src/lib/ui/Badge";
import { Button } from "@src/lib/ui/Button";
import { Card } from "@src/lib/ui/Card";
import Text from "@src/lib/ui/Text";
import { useCommsOptInHomeCardQuery } from "./CommsOptInHomeCard.generated";
import HaveAQuestionButton from "./HaveAQuestionButton";
import useCanOptIntoComms from "./useCanOptIntoComms";
import { WHATS_CHANGING_SECTIONS } from "./WhatsChangingDialog";
import { formatLaunchAt } from "./launchPick";
import { CommsOptInLaunchType, CommsOptInStatus } from "@src/types.generated";

const CommsOptInHomeCard: FC = () => {
  const { canOptIn } = useCanOptIntoComms();
  const { data } = useCommsOptInHomeCardQuery({ skip: !canOptIn });
  if (!canOptIn) return null;
  const commsOptIn = data?.CommsOptIn;
  const timeZone = data?.Me?.user?.org?.timezone;
  const scheduled = commsOptIn?.status === CommsOptInStatus.Scheduled;
  if (
    commsOptIn?.status === CommsOptInStatus.Enabled ||
    (scheduled && commsOptIn.launchType === CommsOptInLaunchType.Now)
  ) {
    return null;
  }
  const launching =
    scheduled && commsOptIn.launchAt && timeZone
      ? `Launching ${formatLaunchAt(commsOptIn.launchAt, timeZone, {
          midSentence: true,
        })}`
      : null;
  return (
    <Card className="flex shrink-0 overflow-hidden">
      <div className="flex min-w-0 flex-1 basis-0 flex-col gap-8 p-7.5">
        <div className="flex flex-col items-start gap-3">
          <div className="flex items-center justify-between gap-3 self-stretch">
            <Badge variant="success">New</Badge>
            {launching && (
              <Text
                variant="compact-description"
                className="text-text-secondary"
              >
                {launching}
              </Text>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Text variant="display" className="text-text-primary">
              Enable Newsfeed and Messages
            </Text>
            <Text
              variant="body-description"
              multiline
              className="text-text-secondary"
            >
              Share company news in the Newsfeed and keep store conversations in
              group and direct messages. Set it up in four steps and launch when
              you&apos;re ready.
            </Text>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button asChild>
            <Link href="/comms-opt-in">
              {scheduled
                ? "View setup"
                : commsOptIn
                  ? "Continue"
                  : "Get started"}
            </Link>
          </Button>
          <div className="flex-1" />
          <HaveAQuestionButton appearance="link" />
        </div>
      </div>
      <div className="relative flex-1 basis-0 bg-bg-tertiary">
        <img
          src={WHATS_CHANGING_SECTIONS[0].image}
          alt={WHATS_CHANGING_SECTIONS[0].title}
          className="absolute inset-0 size-full object-cover object-top"
        />
      </div>
    </Card>
  );
};

gql`
  query CommsOptInHomeCard {
    CommsOptIn {
      id
      status
      launchType
      launchAt
    }
    Me {
      user {
        id
        org {
          id
          timezone
        }
      }
    }
  }
`;

export default CommsOptInHomeCard;
